import { useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Smartphone, Copy, Check, Printer, Share, PlusSquare, MoreVertical } from 'lucide-react'

/**
 * AP-17 (Baustein C) — „Mitarbeiter-App verteilen".
 *
 * Zeigt einen QR-Code + Kurzlink zur Stempel-PWA (pwa.symphenia.com), damit
 * Manager/Admins die App schnell auf die Handys ihrer Mitarbeiter bringen:
 * QR scannen → PWA öffnet → per „App installieren"-Button installieren.
 * Enthält eine druckbare Kurzanleitung (rein clientseitig, kein Backend).
 */

/** Ermittelt die PWA-URL: explizit via VITE_PWA_URL, sonst aus dem aktuellen
 *  Host abgeleitet (app.* → pwa.*), lokal Fallback auf den PWA-Dev-Port. */
function getPwaUrl(): string {
  const env = import.meta.env.VITE_PWA_URL as string | undefined
  if (env) return env.replace(/\/$/, '')
  if (typeof window !== 'undefined') {
    const { origin, hostname, protocol } = window.location
    if (hostname.startsWith('app.')) return origin.replace('app.', 'pwa.')
    if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:5174`
  }
  return 'https://pwa.symphenia.com'
}

export default function EmployeeAppPage() {
  const pwaUrl = getPwaUrl()
  const qrRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(pwaUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* Clipboard nicht verfügbar → ignorieren */
    }
  }

  function handlePrint() {
    const svg = qrRef.current?.querySelector('svg')?.outerHTML ?? ''
    const win = window.open('', '_blank', 'width=480,height=680')
    if (!win) return
    // Hinweis: das schliessende Script-Tag unten steht als <\x2Fscript> im Quelltext.
    // Ausgegeben wird </script>; im Quelltext vermeidet es, dass ein umgebender
    // Script-Block vorzeitig endet, falls das Bundle je inline ausgeliefert wird.
    win.document.write(`
      <html>
        <head>
          <title>Symphenia Stempel-App</title>
          <style>
            * { font-family: system-ui, -apple-system, Arial, sans-serif; }
            body { margin: 0; padding: 32px; text-align: center; color: #1a1a1a; }
            h1 { font-size: 20px; margin: 0 0 4px; }
            p  { font-size: 14px; color: #555; margin: 4px 0; }
            .qr { margin: 24px auto; width: 240px; height: 240px; }
            .url { font-size: 15px; font-weight: 600; word-break: break-all; margin-top: 8px; }
            ol { text-align: left; max-width: 300px; margin: 24px auto 0; font-size: 14px; line-height: 1.6; }
          </style>
        </head>
        <body>
          <h1>Symphenia Stempel-App</h1>
          <p>Mit dem Handy scannen und installieren</p>
          <div class="qr">${svg}</div>
          <p class="url">${pwaUrl}</p>
          <ol>
            <li>QR-Code mit der Handy-Kamera scannen</li>
            <li>Seite öffnet sich im Browser</li>
            <li>Auf „App installieren" tippen</li>
            <li>Mit den eigenen Zugangsdaten anmelden</li>
          </ol>
          <script>window.onload = function () { window.print(); }<\x2Fscript>
        </body>
      </html>
    `)
    win.document.close()
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Mitarbeiter-App verteilen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bring die Stempel-App schnell auf die Handys deiner Mitarbeiter – QR scannen, öffnen,
          installieren.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* QR-Karte */}
        <div className="lg:col-span-1">
          <div className="rounded-lg border bg-card p-6 text-center shadow-sm">
            <div ref={qrRef} className="mx-auto flex w-fit items-center justify-center rounded-lg bg-white p-4">
              <QRCodeSVG value={pwaUrl} size={200} level="M" marginSize={0} />
            </div>

            <p className="mt-4 break-all text-sm font-semibold text-foreground">{pwaUrl}</p>

            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              >
                {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                {copied ? 'Kopiert' : 'Link kopieren'}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              >
                <Printer size={14} />
                Drucken
              </button>
            </div>
          </div>
        </div>

        {/* Erklärung + Anleitung */}
        <div className="space-y-6 lg:col-span-2">
          {/* ERP vs. PWA – Klarstellung (Baustein 5) */}
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-foreground">
              <Smartphone size={18} className="text-primary" />
              Welche App ist das?
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Der QR-Code führt zur <strong className="text-foreground">Symphenia Stempel-App</strong>{' '}
              (<span className="font-mono text-xs">{pwaUrl.replace(/^https?:\/\//, '')}</span>) – die
              schlanke App zum Ein- und Ausstempeln für <strong className="text-foreground">Mitarbeiter</strong>.
              Das ist bewusst <em>nicht</em> das vollständige ERP (dieses Programm hier), sondern nur die
              Zeiterfassung fürs Handy.
            </p>
          </div>

          {/* So installiert der Mitarbeiter */}
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h2 className="mb-4 text-base font-semibold text-foreground">
              So installiert dein Mitarbeiter die App
            </h2>
            <ol className="space-y-3 text-sm text-foreground">
              <Step n={1}>QR-Code mit der Handy-Kamera scannen – die Stempel-App öffnet sich im Browser</Step>
              <Step n={2}>
                Auf <span className="font-medium">„App installieren"</span> tippen (Button auf der
                Anmelde-Seite)
              </Step>
              <Step n={3}>
                <span className="font-medium">Android/Chrome:</span> Systemabfrage mit{' '}
                <span className="inline-flex items-center gap-1 font-medium">
                  Menü <MoreVertical size={13} className="inline text-primary" />
                </span>{' '}
                bestätigen · <span className="font-medium">iPhone/Safari:</span> über{' '}
                <span className="inline-flex items-center gap-1 font-medium">
                  Teilen <Share size={13} className="inline text-primary" />
                </span>{' '}
                →{' '}
                <span className="inline-flex items-center gap-1 font-medium">
                  „Zum Home-Bildschirm" <PlusSquare size={13} className="inline text-primary" />
                </span>
              </Step>
              <Step n={4}>Mit den eigenen Zugangsdaten anmelden – fertig</Step>
            </ol>
            <p className="mt-4 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              Tipp: Über <strong className="text-foreground">Drucken</strong> gibt es ein Blatt mit
              QR-Code und Kurzanleitung zum Aushängen.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {n}
      </span>
      <span>{children}</span>
    </li>
  )
}
